import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, CircularProgress, Breadcrumbs, Link, Tabs, Tab, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MasterTemplateCard, MasterTemplateCardProps, getIsFree } from '../../../components/Experiences/Cards/MasterTemplateCard/MasterTemplateCard';
import { CollectionCard } from '../../../components/Experiences/Cards/CollectionCard/CollectionCard';
import { SearchCourses, SearchOption } from '../../../components/Experiences/SearchCourses/SearchCourses';
import { SelectComponent, SelectOption } from '../../../components/core/SelectComponent/SelectComponent';
import CollectionsDefaultIcon from '../../../components/Experiences/Cards/MasterTemplateCard/Images/collections-default.svg';
import LibraryIcon from './Images/library.svg';
import AlertComponent from '../../../components/core/AlertComponent/AlertComponent';
import AlertService from '../../../components/core/AlertComponent/AlertService';
import { mastersStyles } from './Masters.styles';
import { getClassCollections, getUserCollections, getAvailableCollections, addCollection, removeCollection, Collection, Template, CollectionMember, OrgCollection, OrgCollectionWithLibraryFlag } from '../CollectionsService';
import {
  getState,
  saveState,
  BreadcrumbItem as ServiceBreadcrumbItem,
  getMasterAssignment,
  getMasterAssignmentByTemplateId,
  getStates,
  ClassStateResponse,
  publishExperience,
  PublishExperienceParams,
} from './MastersService';
import {
  areScrollThumbStatesEqual,
  getAngularService,
  getScrollThumbState,
  isPositiveInteger,
  ScrollThumbState,
  toOptionalNumber,
  toRequiredNumber,
} from '../../../services/Utilities';
import { CARD_SCROLLBAR_THUMB_HEIGHT_PX } from '../CardListScrollbar.constants';
import { getUserPermissions } from '../../../services/PermissionsService';
import { getAssignableUserLicenses } from '../../../services/LicensingService';
import type { LicensePlan } from '../../../services/LicensingService';

export interface MasterExperience {
  id: number | string;
  name: string;
  description?: string;
  imageUrl?: string;
  kind: 'collection' | 'template';
  members?: MasterExperience[];
  config?: MasterTemplateConfig;
}

type MasterTemplateConfig = Omit<
  Pick<
    MasterTemplateCardProps,
    'isGuided' |
    'hasGroups' |
    'readingGroupId' |
    'standardSets' |
    'templateId' |
    'gradeId' |
    'standardSetId' |
    'standardSetLabel' |
    'standards' |
    'labels' |
    'parents' |
    'permissions' |
    'source' |
    'isPublic' |
    'aiPreviewState'
  >,
  'templateId'
> & {
  templateId: number;
};

export interface BreadcrumbItem {
  id: number | string | null;
  name: string;
}

export interface AssignClickParams {
  templateId: number;
  parents?: string[];
  isPublic?: boolean;
  licenseId?: number;
}

export interface MastersProps {
  onTemplateClick?: (template: MasterExperience) => void;
  onAssignClick?: (params: AssignClickParams) => void;
  onLicenseToAssignClick?: (params: AssignClickParams) => void | Promise<unknown>;
  onNavigateToExperience?: (params: { experienceId: number; classId?: number }) => void;
  userId: number;
  classId?: number;
  showManageCollections?: boolean;
  assignExperienceText?: string;
}

interface AngularTourService {
  gotoNextTourStep: () => void;
  canNavigate: (selector: string) => boolean;
  isTouring: () => boolean;
}

interface AngularCurrentUser {
  school_id?: unknown;
}

interface AngularUserService {
  getCurrentUser: () => AngularCurrentUser;
  getStateId: () => unknown;
}

interface CollectionState {
  state_id: number;
}

function getMetadataDescription(item: Record<string, unknown>): string | undefined {
  const directDescription = item.description;

  if (typeof directDescription === 'string' && directDescription.trim()) {
    return directDescription;
  }

  const descriptionFields = [
    item.course_description,
    item.unit_description,
    item.collection_description,
    item.meta_description,
  ];

  const fieldDescription = descriptionFields.find((description) =>
    typeof description === 'string' && description.trim()
  );

  if (typeof fieldDescription === 'string') {
    return fieldDescription;
  }

  const attributes = item.attributes;
  if (Array.isArray(attributes)) {
    const attributeDescription = attributes
      .map((attribute) => (attribute as Record<string, unknown>).description)
      .find((description) => typeof description === 'string' && description.trim());

    if (typeof attributeDescription === 'string') {
      return attributeDescription;
    }
  }

  return undefined;
}

function getCollectionIdentity(collection: Record<string, unknown>): string {
  return String(collection.collection_uuid || collection.uuid || collection.id || '').toUpperCase();
}

function getCollectionUuidKey(collectionUuid: string): string {
  return String(collectionUuid).toUpperCase();
}

function toPositiveIdOrZero(value: unknown): number {
  const parsedId = toOptionalNumber(value);
  return parsedId !== undefined && Number.isFinite(parsedId) && parsedId > 0 ? parsedId : 0;
}

function mapStateToOption(state: ClassStateResponse): SelectOption<number> {
  return {
    id: toPositiveIdOrZero(state.id),
    name: state.state_label,
  };
}

function collectionMatchesSelectedState(
  collection: OrgCollectionWithLibraryFlag,
  selectedStateId: number
): boolean {
  const states = Array.isArray(collection.states)
    ? collection.states as CollectionState[]
    : [];
  return states.some((state) => state.state_id === selectedStateId);
}

function getManageCollectionsText(assignExperienceText: string): string {
  const className = assignExperienceText.replace(/^Assign an Experience to\s+/i, '').trim();
  return className
    ? `Choose which collections appear in Assign Experiences for ${className}.`
    : 'Choose which collections appear in Assign Experiences.';
}

export const Masters = ({
  onTemplateClick,
  onAssignClick,
  onLicenseToAssignClick,
  onNavigateToExperience,
  userId,
  classId,
  showManageCollections = false,
  assignExperienceText = 'Assign an Experience to one or more classes',
}: MastersProps) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [rawCollections, setRawCollections] = useState<Collection[]>([]);
  const [rawTemplates, setRawTemplates] = useState<Template[]>([]);
  const [currentItems, setCurrentItems] = useState<MasterExperience[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: 'Library' }]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [availableCollections, setAvailableCollections] = useState<OrgCollectionWithLibraryFlag[]>([]);
  const [catalogClassId, setCatalogClassId] = useState<number | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [stateOptions, setStateOptions] = useState<SelectOption<number>[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<number>(0);
  const [pendingScrollItemId, setPendingScrollItemId] = useState<number | string | null>(null);
  const [highlightedItemId, setHighlightedItemId] = useState<number | string | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[] | null>(null);
  const [assignableLicenses, setAssignableLicenses] = useState<LicensePlan[]>([]);
  const [pendingCatalogCollectionIds, setPendingCatalogCollectionIds] = useState<Set<string>>(() => new Set());
  const hasFetchedCatalogRef = useRef<boolean>(false);
  const classRequestVersionRef = useRef<number>(0);
  const hasFetchedStatesRef = useRef<boolean>(false);
  const isFetchingStatesRef = useRef<boolean>(false);
  const inPreviewRef = useRef<boolean>(false);
  const catalogCollectionRequestRef = useRef<Map<string, number>>(new Map());
  const rawCollectionsRef = useRef<Collection[]>([]);
  const itemRefs = useRef<Map<number | string, HTMLDivElement | null>>(new Map());
  const assignTabContentRef = useRef<HTMLDivElement | null>(null);
  const catalogTabContentRef = useRef<HTMLDivElement | null>(null);
  const [assignScrollThumb, setAssignScrollThumb] = useState<ScrollThumbState>({ top: 0, isVisible: false });
  const [catalogScrollThumb, setCatalogScrollThumb] = useState<ScrollThumbState>({ top: 0, isVisible: false });
  const tourService = useMemo(() => {
    const service = getAngularService<AngularTourService>('tourService');
    if (!service) {
      throw new Error('tourService is required');
    }
    return service;
  }, []);
  const userService = useMemo(() => getAngularService<AngularUserService>('User'), []);
  const normalizedUserSchoolId = toPositiveIdOrZero(userService?.getCurrentUser?.()?.school_id);
  const normalizedTeacherStateId = toPositiveIdOrZero(userService?.getStateId?.());
  const hasTeacherSchoolState = normalizedUserSchoolId > 0 && normalizedTeacherStateId > 0;
  const shouldShowStateDropdown = !hasTeacherSchoolState;
  const catalogStateId = shouldShowStateDropdown ? toPositiveIdOrZero(selectedStateId) : normalizedTeacherStateId;
  const normalizedClassId = isPositiveInteger(classId)
    ? classId
    : null;

  useEffect(() => {
    // Invalidate requests even when switching away and back to the same class.
    classRequestVersionRef.current += 1;
    hasFetchedCatalogRef.current = false;
    setCatalogClassId(null);
    setAvailableCollections([]);
    setCatalogError(null);
    setCatalogLoading(false);
    catalogCollectionRequestRef.current.clear();
    setPendingCatalogCollectionIds(new Set());
    rawCollectionsRef.current = [];
  }, [normalizedClassId]);

  const fetchAssignableCollections = useCallback(async (): Promise<{ collections: Collection[]; templates: Template[] }> => {
    if (normalizedClassId) {
      const classCollectionsResponse = await getClassCollections(normalizedClassId);
      return {
        collections: classCollectionsResponse.collections || [],
        templates: [],
      };
    }

    const userCollectionsResponse = await getUserCollections();
    return {
      collections: userCollectionsResponse.collections || [],
      templates: userCollectionsResponse.templates || [],
    };
  }, [normalizedClassId]);

  const updateAssignScrollThumb = useCallback(() => {
    const nextThumb = getScrollThumbState(assignTabContentRef.current, CARD_SCROLLBAR_THUMB_HEIGHT_PX);
    setAssignScrollThumb((currentThumb) => (
      areScrollThumbStatesEqual(currentThumb, nextThumb) ? currentThumb : nextThumb
    ));
  }, []);

  const updateCatalogScrollThumb = useCallback(() => {
    const nextThumb = getScrollThumbState(catalogTabContentRef.current, CARD_SCROLLBAR_THUMB_HEIGHT_PX);
    setCatalogScrollThumb((currentThumb) => (
      areScrollThumbStatesEqual(currentThumb, nextThumb) ? currentThumb : nextThumb
    ));
  }, []);


  const launchExperiencePreview = useCallback(async (templateId: number, parents: string[] | undefined, isReview: boolean) => {
    const masterCollectionId = parents?.[0] ?? '';

    try {
      const masterData = masterCollectionId
        ? await getMasterAssignment(templateId, masterCollectionId)
        : await getMasterAssignmentByTemplateId(templateId);

      const experienceParams: PublishExperienceParams = {
        parents: parents,
        teacher_user_id: userId,
        catalog_item_id: templateId,
        template_id: templateId,
        name: masterData.name + ' (Preview)',
        section_name: 'Preview of ' + masterData.name,
        starts_at: new Date().toString(),
        ends_at: null,
        description: masterData.description,
        notification: masterData.notification,
        small_groups: 0,
        is_guided: masterData.is_guided,
        preview: true,
        review: isReview,
        return_url: null,
        project_id: masterData.project_id,
        class_id: normalizedClassId,
        students: '',
      };

      const result = await publishExperience(experienceParams);
      const experienceId = result.id;

      onNavigateToExperience?.({
        experienceId,
        classId: normalizedClassId ?? undefined,
      });
    } catch (err) {
      console.error(`Failed to ${isReview ? 'review' : 'preview'} experience:`, err);
      AlertService.error(`Failed to ${isReview ? 'review' : 'preview'} this experience. Please try again.`, true);
    } finally {
      inPreviewRef.current = false;
    }
  }, [userId, normalizedClassId, onNavigateToExperience]);

  const handlePreviewClick = useCallback(async (templateId: number, parents?: string[]) => {
    if (inPreviewRef.current) {
      return;
    }
    if (!tourService.canNavigate('#preview')) {
      return;
    }
    inPreviewRef.current = true;
    await launchExperiencePreview(templateId, parents, false);
  }, [launchExperiencePreview, tourService]);

  const handleReviewClick = useCallback(async (templateId: number, parents?: string[]) => {
    if (inPreviewRef.current) {
      return;
    }
    inPreviewRef.current = true;
    await launchExperiencePreview(templateId, parents, true);
  }, [launchExperiencePreview]);

  const getLibraryCollectionUuidSet = (collections: Collection[]): Set<string> => {
    return new Set(
      collections
        .map((collection) => getCollectionIdentity(collection))
        .filter((collectionId) => !!collectionId)
    );
  };

  // Catalog courses can load before the Library tree, so refresh add/remove state when the Library tree arrives.
  useEffect(() => {
    if (availableCollections.length === 0) {
      return;
    }

    const libraryCollectionIds = getLibraryCollectionUuidSet(rawCollections);
    setAvailableCollections((prev) => {
      let hasChanges = false;
      const updatedCollections = prev.map((collection) => {
        const inLibrary = libraryCollectionIds.has(getCollectionIdentity(collection));
        if (collection.inLibrary === inLibrary) {
          return collection;
        }

        hasChanges = true;
        return {
          ...collection,
          inLibrary,
        };
      });

      return hasChanges ? updatedCollections : prev;
    });
  }, [rawCollections]);

  useEffect(() => {
    if (!showManageCollections && activeTab === 1) {
      setActiveTab(0);
    }
  }, [showManageCollections, activeTab]);

  useEffect(() => {
    let isCurrentRequest = true;

    // Cache only for the current class; an open catalog reloads when that class changes.
    if (showManageCollections && normalizedClassId && activeTab === 1 && !hasFetchedCatalogRef.current) {
      setCatalogLoading(true);
      setCatalogError(null);
      getAvailableCollections(normalizedClassId)
        .then((collections) => {
          if (!isCurrentRequest) {
            return;
          }
          const libraryCollectionIds = getLibraryCollectionUuidSet(rawCollectionsRef.current);
          const collectionsWithFlag: OrgCollectionWithLibraryFlag[] = collections.map((collection) => ({
            ...collection,
            inLibrary: libraryCollectionIds.has(getCollectionIdentity(collection)),
          }));
          setAvailableCollections(collectionsWithFlag);
          setCatalogClassId(normalizedClassId);
          hasFetchedCatalogRef.current = true;
        })
        .catch((err) => {
          if (!isCurrentRequest) {
            return;
          }
          hasFetchedCatalogRef.current = false;
          setCatalogError(err instanceof Error ? err.message : 'Failed to load collections');
        })
        .finally(() => {
          if (isCurrentRequest) {
            setCatalogLoading(false);
          }
        });
    }

    return () => {
      isCurrentRequest = false;
    };
  }, [showManageCollections, normalizedClassId, activeTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Teachers with no school or no school state need the manual state picker.
    if (showManageCollections && newValue === 1 && shouldShowStateDropdown && !hasFetchedStatesRef.current && !isFetchingStatesRef.current) {
      isFetchingStatesRef.current = true;
      getStates()
        .then((states) => {
          const mappedStates = states
            .map((state) => mapStateToOption(state))
            .filter((state) => state.id > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
          setStateOptions(mappedStates);
          const defaultSelectedState = mappedStates.find((state) => state.id === normalizedTeacherStateId) ?? mappedStates[0];
          if (defaultSelectedState) {
            setSelectedStateId(defaultSelectedState.id);
          }
          hasFetchedStatesRef.current = true;
        })
        .catch(() => {
          setStateOptions([]);
          hasFetchedStatesRef.current = false;
        })
        .finally(() => {
          isFetchingStatesRef.current = false;
        });
    }

  };

  const mapToMasterExperience = useCallback((item: Collection | Template | CollectionMember): MasterExperience => {
    const kind = (item as CollectionMember).kind || 'template';
    const members = (item as CollectionMember).members;
    const itemWithConfig = item as Record<string, unknown>;
    const gradeId = itemWithConfig.gradeId ?? itemWithConfig.grade_id;

    const config: MasterTemplateConfig | undefined = kind === 'template' ? {
      isGuided: itemWithConfig.is_guided as boolean | undefined,
      hasGroups: itemWithConfig.has_groups as boolean | undefined,
      readingGroupId: toOptionalNumber(itemWithConfig.reading_group_id),
      standardSets: itemWithConfig.standard_sets as string | null | undefined,
      templateId: toRequiredNumber(itemWithConfig.template_id, `template_id for template ${String(item.id)}`),
      gradeId: toOptionalNumber(gradeId),
      standardSetId: toOptionalNumber(itemWithConfig.standard_set_id),
      standardSetLabel: itemWithConfig.standard_set_label as string | undefined,
      standards: itemWithConfig.standards as MasterTemplateConfig['standards'],
      labels: itemWithConfig.labels as MasterTemplateConfig['labels'],
      parents: itemWithConfig.parents as string[] | undefined,
      permissions: itemWithConfig.permissions as string[] | undefined,
      source: itemWithConfig.source as string | undefined,
      isPublic: itemWithConfig.is_public as boolean | undefined,
      aiPreviewState: itemWithConfig.ai_preview_state as string | null | undefined,
    } : undefined;

    const imageUrl = kind === 'template'
      ? (itemWithConfig.image_filename as string | undefined)
      : item.image_url;

    return {
      id: item.id,
      name: item.name,
      description: getMetadataDescription(itemWithConfig),
      imageUrl,
      kind: kind as 'collection' | 'template',
      members: members?.map((m) => mapToMasterExperience(m)),
      config,
    };
  }, []);

  const getTopLevelItems = useCallback((): MasterExperience[] => {
    const collections = rawCollections.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));
    const templates = rawTemplates.map((t) => mapToMasterExperience({ ...t, kind: 'template' }));
    return [...collections, ...templates];
  }, [rawCollections, rawTemplates, mapToMasterExperience]);

  const hasRestoredRef = useRef<boolean>(false);
  const isInitializedRef = useRef<boolean>(false);

  const navigateToBreadcrumbs = useCallback((
    targetBreadcrumbs: ServiceBreadcrumbItem[],
    collections: Collection[]
  ) => {
    if (targetBreadcrumbs.length <= 1) {
      return null;
    }

    let items: MasterExperience[] = collections.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));

    for (let i = 1; i < targetBreadcrumbs.length; i++) {
      const crumb = targetBreadcrumbs[i];
      const collection = items.find((item) => item.kind === 'collection' && String(item.id) === String(crumb.id));
      if (!collection) {
        return null;
      }
      items = collection.members || [];
    }

    const sortedItems = [
      ...items.filter((m) => m.kind === 'collection'),
      ...items.filter((m) => m.kind === 'template'),
    ];

    return sortedItems;
  }, [mapToMasterExperience]);

  const refreshUserCollections = useCallback(async (shouldApply: () => boolean = () => true) => {
    const classRequestVersion = classRequestVersionRef.current;
    try {
      const { collections: collectionsData, templates: templatesData } = await fetchAssignableCollections();
      if (classRequestVersion !== classRequestVersionRef.current || !shouldApply()) {
        return;
      }

      rawCollectionsRef.current = collectionsData;
      setRawCollections(collectionsData);
      setRawTemplates(templatesData);

      const libraryCollectionIds = getLibraryCollectionUuidSet(collectionsData);
      setAvailableCollections((prev) =>
        prev.map((collection) => ({
          ...collection,
          inLibrary: libraryCollectionIds.has(getCollectionIdentity(collection)),
        }))
      );

      const mappedCollections = collectionsData.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));
      const mappedTemplates = templatesData.map((t) => mapToMasterExperience({ ...t, kind: 'template' }));

      if (breadcrumbs.length === 1) {
        setCurrentItems([...mappedCollections, ...mappedTemplates]);
        return;
      }

      const refreshedItems = navigateToBreadcrumbs(breadcrumbs as ServiceBreadcrumbItem[], collectionsData);
      if (refreshedItems) {
        setCurrentItems(refreshedItems);
      } else {
        setCurrentItems([...mappedCollections, ...mappedTemplates]);
        setBreadcrumbs([{ id: null, name: 'Library' }]);
      }
    } catch (err) {
      console.error('Failed to refresh user collections:', err);
    }
  }, [breadcrumbs, fetchAssignableCollections, mapToMasterExperience, navigateToBreadcrumbs]);

  const setCatalogCollectionPending = useCallback((collectionKey: string, isPending: boolean) => {
    setPendingCatalogCollectionIds((prev) => {
      const nextPendingIds = new Set(prev);
      if (isPending) {
        nextPendingIds.add(collectionKey);
      } else {
        nextPendingIds.delete(collectionKey);
      }
      return nextPendingIds;
    });
  }, []);

  const handleShowInAssignExperienceChange = useCallback(async (
    collectionUuid: string,
    showInAssignExperience: boolean
  ) => {
    if (!normalizedClassId) {
      return;
    }

    const collectionKey = getCollectionUuidKey(collectionUuid);
    const classRequestVersion = classRequestVersionRef.current;
    // Track the latest request per course so slow responses cannot overwrite a newer checkbox intent.
    const requestId = (catalogCollectionRequestRef.current.get(collectionKey) || 0) + 1;
    catalogCollectionRequestRef.current.set(collectionKey, requestId);
    setCatalogCollectionPending(collectionKey, true);

    const isLatestRequest = () => classRequestVersion === classRequestVersionRef.current
      && catalogCollectionRequestRef.current.get(collectionKey) === requestId;

    try {
      if (showInAssignExperience) {
        await addCollection(collectionUuid, normalizedClassId);
      } else {
        await removeCollection(collectionUuid, normalizedClassId);
      }

      if (!isLatestRequest()) {
        return;
      }

      setAvailableCollections((prev) =>
        prev.map((c) =>
          getCollectionIdentity(c) === collectionKey ? { ...c, inLibrary: showInAssignExperience } : c
        )
      );
      await refreshUserCollections(isLatestRequest);
    } catch (err) {
      console.error(
        `Failed to ${showInAssignExperience ? 'add collection to' : 'remove collection from'} library:`,
        err
      );
    } finally {
      if (isLatestRequest()) {
        catalogCollectionRequestRef.current.delete(collectionKey);
        setCatalogCollectionPending(collectionKey, false);
      }
    }
  }, [normalizedClassId, refreshUserCollections, setCatalogCollectionPending]);

  useEffect(() => {
    let isCurrentRequest = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { collections: collectionsData, templates: templatesData } = await fetchAssignableCollections();
        if (!isCurrentRequest) {
          return;
        }

        rawCollectionsRef.current = collectionsData;
        setRawCollections(collectionsData);
        setRawTemplates(templatesData);
        const isTouring = tourService.isTouring();
        const mappedCollections = collectionsData.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));
        const mappedTemplates = templatesData.map((t) => mapToMasterExperience({ ...t, kind: 'template' }));

        // Restore prior breadcrumb navigation when possible; fall back to top-level items if saved state is invalid or absent.
        const savedState = getState();
        if (!isTouring && savedState.breadcrumbs.length > 1 && !hasRestoredRef.current) {
          hasRestoredRef.current = true;
          const restoredItems = navigateToBreadcrumbs(savedState.breadcrumbs, collectionsData);
          if (restoredItems) {
            setCurrentItems(restoredItems);
            setBreadcrumbs(savedState.breadcrumbs);
          } else {
            setCurrentItems([...mappedCollections, ...mappedTemplates]);
            setBreadcrumbs([{ id: null, name: 'Library' }]);
          }
        } else {
          setCurrentItems([...mappedCollections, ...mappedTemplates]);
          setBreadcrumbs([{ id: null, name: 'Library' }]);
        }
        isInitializedRef.current = true;
      } catch (err) {
        if (!isCurrentRequest) {
          return;
        }
        console.error('Failed to fetch data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load experiences');
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
          tourService.gotoNextTourStep();
        }
      }
    };

    fetchData();
    return () => {
      isCurrentRequest = false;
    };
  }, [fetchAssignableCollections, mapToMasterExperience, navigateToBreadcrumbs, tourService]);

  useEffect(() => {
    if (isInitializedRef.current) {
      saveState({ breadcrumbs });
    }
  }, [breadcrumbs]);

  useEffect(() => {
    let isMounted = true;

    getUserPermissions(userId)
      .then((permissions) => {
        if (isMounted) {
          setUserPermissions(permissions);
        }
      })
      .catch(() => {
        if (isMounted) {
          setUserPermissions(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    let isMounted = true;

    if (!normalizedClassId) {
      setAssignableLicenses((currentLicenses) => currentLicenses.length ? [] : currentLicenses);
      return () => {
        isMounted = false;
      };
    }

    getAssignableUserLicenses(userId)
      .then((licenses) => {
        if (isMounted) {
          setAssignableLicenses(licenses);
        }
      })
      .catch((err) => {
        console.error('Failed to fetch assignable licenses:', err);
        if (isMounted) {
          setAssignableLicenses([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [userId, normalizedClassId]);

  const scrollItemIntoAssignTab = useCallback((itemId: number | string): boolean => {
    const itemElement = itemRefs.current.get(itemId);
    const assignTabContentElement = assignTabContentRef.current;
    if (!itemElement || !assignTabContentElement) {
      return false;
    }

    const containerRect = assignTabContentElement.getBoundingClientRect();
    const itemRect = itemElement.getBoundingClientRect();
    const targetScrollTop =
      assignTabContentElement.scrollTop +
      (itemRect.top - containerRect.top) -
      (containerRect.height / 2) +
      (itemRect.height / 2);

    assignTabContentElement.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth',
    });

    return true;
  }, []);

  useEffect(() => {
    if (pendingScrollItemId === null) {
      return;
    }
    if (activeTab !== 0) {
      return;
    }

    const didScroll = scrollItemIntoAssignTab(pendingScrollItemId);
    if (!didScroll) {
      return;
    }
    setHighlightedItemId(pendingScrollItemId);
    setPendingScrollItemId(null);
  }, [pendingScrollItemId, currentItems, breadcrumbs, activeTab, loading, scrollItemIntoAssignTab]);

  useEffect(() => {
    if (highlightedItemId === null) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedItemId(null);
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [highlightedItemId]);

  useEffect(() => {
    updateAssignScrollThumb();
    updateCatalogScrollThumb();

    window.addEventListener('resize', updateAssignScrollThumb);
    window.addEventListener('resize', updateCatalogScrollThumb);

    return () => {
      window.removeEventListener('resize', updateAssignScrollThumb);
      window.removeEventListener('resize', updateCatalogScrollThumb);
    };
  }, [
    activeTab,
    availableCollections.length,
    breadcrumbs.length,
    catalogLoading,
    currentItems.length,
    loading,
    updateAssignScrollThumb,
    updateCatalogScrollThumb,
  ]);

  // TODO: Evaluate normalizing root level collection data and all hierarchical data.
  // Currently the root level contains collections and templates as two different arrays.
  // Inside each collection below the root all collections and templates are saved in a
  // members list with each item having a kind type indicating if it is a collection or template.
  const handleCollectionClick = useCallback((collectionId: number | string) => {
    const isAtRoot = breadcrumbs.length === 1;

    if (isAtRoot) {
      const rawCollection = rawCollections.find((c) => String(c.id) === String(collectionId));
      if (rawCollection) {
        const members = (rawCollection.members || []).map((m) => mapToMasterExperience(m));
        const sortedMembers = [
          ...members.filter((m) => m.kind === 'collection'),
          ...members.filter((m) => m.kind === 'template'),
        ];
        setCurrentItems(sortedMembers);
        setBreadcrumbs((prev) => [...prev, { id: rawCollection.id, name: rawCollection.name }]);
        if (!rawCollection.members || rawCollection.members.length === 0 || tourService.canNavigate(rawCollection.members[0].kind)) {
          tourService.gotoNextTourStep();
        }
      }
    } else {
      const collection = currentItems.find((item) => item.kind === 'collection' && String(item.id) === String(collectionId));
      if (collection) {
        const members = collection.members || [];
        const sortedMembers = [
          ...members.filter((m) => m.kind === 'collection'),
          ...members.filter((m) => m.kind === 'template'),
        ];
        setCurrentItems(sortedMembers);
        setBreadcrumbs((prev) => [...prev, { id: collection.id, name: collection.name }]);
        if (!collection.members || collection.members.length === 0 || tourService.canNavigate(collection.members[0].kind)) {
          tourService.gotoNextTourStep();
        }
      }
    }
  }, [breadcrumbs.length, rawCollections, currentItems, mapToMasterExperience, tourService]);

  const handleCardClick = useCallback((experience: MasterExperience) => {
    if (experience.kind === 'collection') {
      handleCollectionClick(experience.id);
    } else {
      onTemplateClick?.(experience);
    }
  }, [handleCollectionClick, onTemplateClick]);

  const findCollectionInHierarchy = useCallback((targetId: number | string, path: BreadcrumbItem[]): MasterExperience | null => {
    let items: MasterExperience[] = rawCollections.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));

    for (let i = 1; i < path.length; i++) {
      const crumb = path[i];
      const collection = items.find((item) => item.kind === 'collection' && String(item.id) === String(crumb.id));
      if (!collection) return null;

      if (String(crumb.id) === String(targetId)) {
        return collection;
      }

      items = collection.members || [];
    }

    return items.find((item) => item.kind === 'collection' && String(item.id) === String(targetId)) || null;
  }, [rawCollections, mapToMasterExperience]);

  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index === 0) {
      setCurrentItems(getTopLevelItems());
      setBreadcrumbs([{ id: null, name: 'Library' }]);
    } else {
      const targetBreadcrumb = breadcrumbs[index];
      if (targetBreadcrumb.id !== null) {
        const pathToTarget = breadcrumbs.slice(0, index + 1);
        const collection = findCollectionInHierarchy(targetBreadcrumb.id, pathToTarget);
        if (collection) {
          const members = collection.members || [];
          const sortedMembers = [
            ...members.filter((m) => m.kind === 'collection'),
            ...members.filter((m) => m.kind === 'template'),
          ];
          setCurrentItems(sortedMembers);
          setBreadcrumbs(pathToTarget);
        }
      }
    }
  }, [breadcrumbs, getTopLevelItems, findCollectionInHierarchy]);

  const searchOptions = useMemo((): SearchOption[] => {
    const options: SearchOption[] = [];
    const rootPath: BreadcrumbItem[] = [{ id: null, name: 'Library' }];

    const traverseCollection = (collection: Collection, currentPath: BreadcrumbItem[]) => {
      const collectionPath = [...currentPath, { id: collection.id, name: collection.name }];

      if (collection.members) {
        for (const member of collection.members) {
          if (member.kind === 'collection') {
            traverseCollection(member as unknown as Collection, collectionPath);
          } else if (member.kind === 'template') {
            options.push({
              id: member.id,
              name: member.name,
              kind: 'template',
              path: collectionPath,
            });
          }
        }
      }
    };

    for (const collection of rawCollections) {
      traverseCollection(collection, rootPath);
    }

    for (const template of rawTemplates) {
      options.push({
        id: template.id,
        name: template.name,
        kind: 'template',
        path: rootPath,
      });
    }

    return options;
  }, [rawCollections, rawTemplates]);

  const handleSearchSelect = useCallback((option: SearchOption) => {
    setActiveTab(0);

    if (option.path.length === 1) {
      const mappedCollections = rawCollections.map((c) => mapToMasterExperience({ ...c, kind: 'collection' }));
      const mappedTemplates = rawTemplates.map((t) => mapToMasterExperience({ ...t, kind: 'template' }));
      setCurrentItems([...mappedCollections, ...mappedTemplates]);
      setBreadcrumbs([{ id: null, name: 'Library' }]);
      setPendingScrollItemId(option.id);
    } else {
      const targetBreadcrumbs = option.path as ServiceBreadcrumbItem[];
      const restoredItems = navigateToBreadcrumbs(targetBreadcrumbs, rawCollections);
      if (restoredItems) {
        setCurrentItems(restoredItems);
        setBreadcrumbs(option.path);
        setPendingScrollItemId(option.id);
      } else {
        setPendingScrollItemId(null);
      }
    }
  }, [rawCollections, rawTemplates, mapToMasterExperience, navigateToBreadcrumbs]);

  const collections = currentItems.filter((item) => item.kind === 'collection');
  const templates = currentItems.filter((item) => item.kind === 'template');
  const hasNoData = currentItems.length === 0;
  const filteredAvailableCollections = useMemo(
    () => {
      if (catalogClassId !== normalizedClassId) {
        return [];
      }
      // Subscribed courses must remain addable even when their state metadata differs or is missing.
      return catalogStateId === 0
        ? availableCollections
        : availableCollections.filter((collection) => collection.isSubscribed === true || collectionMatchesSelectedState(collection, catalogStateId));
    },
    [availableCollections, catalogClassId, normalizedClassId, catalogStateId]
  );
  const visibleActiveTab = showManageCollections ? activeTab : 0;
  const manageCollectionsText = getManageCollectionsText(assignExperienceText);

  return (
    <Box sx={mastersStyles.container(theme)}>
      <AlertComponent />
      <Box sx={mastersStyles.tabsContainer(theme)}>
        <Tabs value={visibleActiveTab} onChange={handleTabChange} sx={{ flex: 1 }}>
          <Tab label="Assign Experience" sx={mastersStyles.tab(theme)} />
          {showManageCollections && (
            <Tab label="Manage Collections" sx={mastersStyles.tab(theme)} />
          )}
        </Tabs>
        {visibleActiveTab === 0 && (
          <SearchCourses
            options={searchOptions}
            onSelect={handleSearchSelect}
          />
        )}
        {showManageCollections && visibleActiveTab === 1 && shouldShowStateDropdown && (
          <Box sx={mastersStyles.catalogStateFilterContainer()}>
            <SelectComponent
              label="States"
              options={stateOptions}
              value={stateOptions.length === 0 ? undefined : selectedStateId}
              onChange={(selectedStateOptionId) => setSelectedStateId(toPositiveIdOrZero(selectedStateOptionId))}
              placeholder="Select state"
              disabled={stateOptions.length === 0}
            />
          </Box>
        )}
      </Box>

      {visibleActiveTab === 0 && (
        <Box id="course_list" sx={mastersStyles.tabContent(theme)}>
          {loading ? (
            <Box sx={mastersStyles.loadingContainer(theme)}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={mastersStyles.emptyState(theme)}>{error}</Box>
          ) : (
            <>
              {assignExperienceText && (
                <Typography component="p" sx={mastersStyles.assignExperienceText(theme)}>
                  {assignExperienceText}
                </Typography>
              )}
              <Box sx={mastersStyles.breadcrumbContainer(theme)}>
                <Breadcrumbs aria-label="breadcrumb">
                  {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                      <Link
                        key={crumb.id ?? 'library'}
                        id={index === 0 ? 'course_library' : undefined}
                        underline={isLast ? 'none' : 'hover'}
                        sx={mastersStyles.breadcrumbLink(theme)}
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (!isLast) {
                            handleBreadcrumbClick(index);
                          }
                        }}
                        style={{ cursor: isLast ? 'default' : 'pointer' }}
                      >
                        {index === 0 && (
                          <Box
                            component="span"
                            aria-hidden="true"
                            sx={mastersStyles.breadcrumbIcon(theme, LibraryIcon)}
                          />
                        )}
                        {crumb.name}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </Box>

              <Box sx={mastersStyles.scrollFrame()}>
                <Box ref={assignTabContentRef} onScroll={updateAssignScrollThumb} sx={mastersStyles.scrollableList(theme)}>
                  {hasNoData ? (
                    <Box sx={mastersStyles.emptyState(theme)}>No experiences available</Box>
                  ) : (
                    <Box key={breadcrumbs.map((b) => b.id).join('-')} sx={mastersStyles.listContainer(theme)}>
                      {collections.map((collection) => (
                        <Box
                          key={`collection-${collection.id}`}
                          className={String(highlightedItemId) === String(collection.id) ? 'search-highlight' : undefined}
                          ref={(el: HTMLDivElement | null) => { itemRefs.current.set(collection.id, el); }}
                          onClick={() => handleCardClick(collection)}
                          sx={mastersStyles.collectionCardWrapper(theme)}
                        >
                          <CollectionCard
                            name={collection.name}
                            description={collection.description}
                            descriptionMaxLines={3}
                            imageUrl={collection.imageUrl || CollectionsDefaultIcon}
                          />
                        </Box>
                      ))}
                      {templates.map((template) => {
                        return (
                          <Box
                            key={`template-${template.id}`}
                            ref={(el: HTMLDivElement | null) => { itemRefs.current.set(template.id, el); }}
                            onClick={() => handleCardClick(template)}
                            sx={mastersStyles.templateCardWrapper(theme, String(highlightedItemId) === String(template.id))}
                          >
                            <MasterTemplateCard
                              name={template.name}
                              description={template.description}
                              imageUrl={template.imageUrl}
                              {...(template.config || {})}
                              currentUserPermissions={userPermissions}
                              assignDisabled={tourService.isTouring()}
                              assignableLicenses={assignableLicenses}
                              onAssignClick={() => {
                                const templateId = template.config?.templateId ?? Number(template.id);
                                onAssignClick?.({
                                  templateId,
                                  parents: template.config?.parents,
                                  isPublic: getIsFree(template.config?.parents, template.config?.isPublic),
                                });
                              }}
                              onLicenseToAssignClick={async (licenseId) => {
                                const templateId = template.config?.templateId ?? Number(template.id);
                                try {
                                  const licenseResult = await onLicenseToAssignClick?.({
                                    templateId,
                                    parents: template.config?.parents,
                                    licenseId,
                                  });
                                  if (licenseResult !== null && licenseResult !== undefined) {
                                    await refreshUserCollections();
                                  }
                                } catch (err) {
                                  console.error('Failed to refresh class assignable experiences after licensing:', err);
                                }
                              }}
                              onPreviewClick={() => {
                                const templateId = template.config?.templateId ?? Number(template.id);
                                handlePreviewClick(templateId, template.config?.parents);
                              }}
                              onReviewClick={() => {
                                const templateId = template.config?.templateId ?? Number(template.id);
                                handleReviewClick(templateId, template.config?.parents);
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
                <Box
                  aria-hidden={true}
                  sx={mastersStyles.fixedScrollbarThumb(theme, assignScrollThumb.top, assignScrollThumb.isVisible)}
                />
              </Box>
            </>
          )}
        </Box>
      )}

      {showManageCollections && visibleActiveTab === 1 && (
        <Box sx={mastersStyles.tabContent(theme)}>
          <Typography component="p" sx={mastersStyles.manageCollectionsText(theme)}>
            {manageCollectionsText}
          </Typography>
          {catalogLoading ? (
            <Box sx={mastersStyles.loadingContainer(theme)}>
              <CircularProgress />
            </Box>
          ) : catalogError ? (
            <Box sx={mastersStyles.emptyState(theme)}>{catalogError}</Box>
          ) : filteredAvailableCollections.length === 0 ? (
            <Box sx={mastersStyles.emptyState(theme)}>No courses available</Box>
          ) : (
            <Box sx={mastersStyles.scrollFrame()}>
              <Box ref={catalogTabContentRef} onScroll={updateCatalogScrollThumb} sx={mastersStyles.scrollableList(theme)}>
                <Box sx={mastersStyles.listContainer(theme)}>
                  {filteredAvailableCollections.map((collection) => {
                    const collectionUuid = collection.collection_uuid || collection.uuid;
                    const collectionKey = getCollectionUuidKey(collectionUuid);

                    return (
                      <Box key={collection.uuid} sx={mastersStyles.collectionCardWrapper(theme)}>
                        <CollectionCard
                          name={collection.name || 'Unnamed Collection'}
                          description={collection.description}
                          descriptionMaxLines={3}
                          imageUrl={collection.image_filename || CollectionsDefaultIcon}
                          showInAssignExperience={collection.inLibrary}
                          showInAssignExperienceDisabled={!normalizedClassId || pendingCatalogCollectionIds.has(collectionKey)}
                          onShowInAssignExperienceChange={(showInAssignExperience) => {
                            handleShowInAssignExperienceChange(collectionUuid, showInAssignExperience);
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Box
                aria-hidden={true}
                sx={mastersStyles.fixedScrollbarThumb(theme, catalogScrollThumb.top, catalogScrollThumb.isVisible)}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Masters;
